import React from 'react';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { DataTable, RelativeTime } from './shared';
import { formatCount, requestErrorMessage } from '../utils';
import { useInsightsStyles } from '../styles';

export const POLL_INTERVAL_MS = 30000;
const LIMIT = 10;

const useStyles = makeStyles({
  fresh: { animation: '$enter 900ms ease-out both' },
  '@keyframes enter': {
    from: { opacity: 0, transform: 'translateY(-6px)', backgroundColor: 'rgba(61,125,221,.18)' },
    to: { opacity: 1, transform: 'translateY(0)', backgroundColor: 'transparent' },
  },
});

const rowKey = row => `${row.user_id || row.email}|${row.signed_in_at}`;

const columns = [
  { key: 'signed_in_at', label: 'Signed in', render: row => <RelativeTime value={row.signed_in_at} /> },
  { key: 'name', label: 'Name', render: row => <><div style={{ fontWeight: 600 }}>{row.name || '-'}</div>{row.email ? <div style={{ fontSize: '.72rem', color: 'var(--dm-muted, #647184)' }}>{row.email}</div> : null}</> },
  { key: 'role', label: 'Role' },
  { key: 'companies', label: 'Companies', render: row => (row.companies?.length ? row.companies.join(', ') : '-') },
  { key: 'sign_ins_that_day', label: 'Sign-ins that day', align: 'right', render: row => formatCount(row.sign_ins_that_day) },
  { key: 'method', label: 'Method' },
];

/**
 * Starts from the dashboard's cached `logins.latest`, then polls the live endpoint every 30 seconds.
 * Polling pauses while the tab is hidden and resumes with an immediate fetch when it is shown again.
 */
const LatestSignIns = ({ initial = [], service }) => {
  const classes = useInsightsStyles();
  const ownClasses = useStyles();
  const [rows, setRows] = React.useState(initial);
  const [fresh, setFresh] = React.useState(() => new Set());
  const [error, setError] = React.useState('');
  const [stopped, setStopped] = React.useState(false);
  const seen = React.useRef(new Set(initial.map(rowKey)));
  const inFlight = React.useRef(false);
  const mounted = React.useRef(true);

  React.useEffect(() => () => { mounted.current = false; }, []);

  const poll = React.useCallback(async () => {
    if (document.hidden || inFlight.current) return;
    inFlight.current = true;
    try {
      const response = await service.getSuperadminLatestSignIns(LIMIT);
      if (!mounted.current) return;
      const next = response?.data?.sign_ins || [];
      const added = next.map(rowKey).filter(key => !seen.current.has(key));
      added.forEach(key => seen.current.add(key));
      setFresh(new Set(added));
      setRows(next);
      setError('');
    } catch (requestError) {
      if (!mounted.current) return;
      const status = requestError?.response?.status;
      setError(requestErrorMessage(requestError));
      if (status === 401 || status === 403 || status === 400) setStopped(true);
    } finally {
      inFlight.current = false;
    }
  }, [service]);

  React.useEffect(() => {
    if (stopped) return undefined;
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    const onVisibility = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [poll, stopped]);

  return (
    <div data-testid="latest-sign-ins">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <Typography component="h3" className={classes.subheading}>Latest sign-ins</Typography>
        <Typography variant="caption" color="textSecondary">{stopped ? 'Live updates stopped' : 'Updates every 30 seconds'}</Typography>
      </div>
      {error ? <Typography role="alert" className={classes.footnote} style={{ color: '#b91c1c', marginTop: 0, marginBottom: 8 }}>{error}</Typography> : null}
      <DataTable
        ariaLabel="Latest sign-ins"
        columns={columns}
        rows={rows}
        getRowKey={rowKey}
        rowClassName={row => (fresh.has(rowKey(row)) ? ownClasses.fresh : '')}
      />
    </div>
  );
};

export default LatestSignIns;
